import { InfoIcon, eyeIcon } from '@/shared/ui/assets';
import { DOUBLE_HYPHEN_BULLET, MESSAGE_ARTIFACT_REPORT } from '@/shared/config';
import { CustomButton, CustomTooltip } from '@/shared/ui/components';
import { getDuration, zeroPad, generateFullName, dateToString } from '@/shared/lib';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import type { fetchGetUnreviewedReportById, fetchGetUnreviewedReports } from '@/entities/inbox';
import { INSTANT_REPORT_STATUS } from '../config/instantReportTab';
import type {
  FormattedInstantReport,
  InstantReportTableItem,
} from '../ui/instantReportTab/instantReportTab.types';

type RawInstantReport =
  | NonNullable<Awaited<ReturnType<typeof fetchGetUnreviewedReportById>>>
  | NonNullable<NonNullable<Awaited<ReturnType<typeof fetchGetUnreviewedReports>>>[number]>;

/**
 * Flattens an inbox instant report into the shape the report table and viewer consume. Lifts study, patient, event, facility and inbox fields to the top level, builds physician and patient names through generateFullName, formats event time, the date-of-service range and generated time with dateToString in the study timezone, and substitutes the artifact message or the joined event comments for the technician comments.
 *
 * @param report - The raw instant report with nested study, event and inbox data.
 * @returns The flattened report ready for table rendering.
 */
const formatInstantReport = (report: RawInstantReport) => {
  const formattedInstantReport = {
    id: report.id,
    reportId: report.reportId,
    type: report.type,
    start: report.start,
    stop: report.stop,
    studyFid: report.study?.friendlyId,
    physicianId: report.study?.info?.interprettingPhysician?.id,
    interprettingPhysicianName: generateFullName(report.study?.info?.interprettingPhysician),
    patientName: generateFullName(report.study?.info?.patient),
    patientId: report.study?.info?.patient?.patientId,
    study: report.study,
    studyType: report.study?.studyType,
    timezone: report.study?.timezone,
    eventFid: report.event?.friendlyId,
    physicianInterpretation: report.physicianInterpretation,
    technicianComments: report.isArtifactReport
      ? MESSAGE_ARTIFACT_REPORT
      : report.type === 'Event'
        ? _.join(report.event?.comments, ' ')
        : report.technicianComments,
    date: report.inbox?.date,
    priority: report.inbox?.priority,
    isReviewed: report.inbox?.isReviewed,
    isRead: report.inbox?.isRead,
    eventTimeUTC: report.start,
    eventTime: dateToString({
      startStudyDate: report.study?.start ?? undefined,
      isoDate: report.start ?? undefined,
      timezone: report.study?.timezone ?? undefined,
      studyTimeOffset: report.study?.studyTimeOffset ?? undefined,
      facilityCountry: report.study?.facility?.contact?.country ?? undefined,
      facilityTimezone: report.study?.facility?.timezone ?? undefined,
    }),
    dateOfService: `${dateToString({
      isoDate: report.start ?? undefined,
      timezone: report.study?.timezone ?? undefined,
      studyTimeOffset: report.study?.studyTimeOffset ?? undefined,
      format: 'MM/DD/YY',
    })} - ${dateToString({
      isoDate: report.stop ?? undefined,
      timezone: report.study?.timezone ?? undefined,
      studyTimeOffset: report.study?.studyTimeOffset ?? undefined,
      format: 'MM/DD/YY',
    })}`,
    eventId: report.event?.id,
    hasClinicData: report.event?.hasClinicData,
    isNotificationReport: report.type === 'Notification',
    isBiofluxDirect: report.study?.isBiofluxDirect,
    hasSignature: report.hasSignature,
    isArtifactReport: report.isArtifactReport,
    facility: report.study?.facility,
    facilityName: report.study?.facility?.name,
    lastModified: report.lastModified,
    status: report.status,
    formatVersion: report.formatVersion,
    aiFormatVersion: report.aiFormatVersion,
    generatedTime: dateToString({
      startStudyDate: report.study?.start ?? undefined,
      isoDate: report.createdAt ?? undefined,
      timezone: report.study?.timezone ?? undefined,
      studyTimeOffset: report.study?.studyTimeOffset ?? undefined,
      facilityCountry: report.study?.facility?.contact?.country ?? undefined,
      facilityTimezone: report.study?.facility?.timezone ?? undefined,
    }),
    createdBy: report.createdBy,
    timeMonitored: report.timeMonitored,
  };
  // FormattedInstantReport declares these fields as non-nullable, but callers throughout
  // this file already handle them defensively (e.g. via ?. / _.isNil checks) when they're
  // actually missing, so the type here is asserted rather than the values coerced.
  return formattedInstantReport as unknown as FormattedInstantReport;
};

/**
 * Maps a list of raw instant reports onto the flattened table shape. Iterates the input with lodash forEach and pushes each formatInstantReport result, which lifts study, patient, event and formatted date fields to the top level, into a new array.
 *
 * @param reports - The raw instant reports to flatten.
 * @returns The flattened reports in input order.
 */
const formatInstantReports = (reports: RawInstantReport[]) => {
  const formattedReports: FormattedInstantReport[] = [];
  _.forEach(reports, (report) => {
    const formattedReport = formatInstantReport(report);
    formattedReports.push(formattedReport);
  });
  return formattedReports;
};

/**
 * Renders the report-id cell of the instant-report table. Shows a skeleton until the table reports loaded data, zero-pads a non-nil id or falls back to the double-hyphen bullet, and greys the text with the disable-column class when the report's status is not GENERATED.
 *
 * @param cell - The report's friendly id.
 * @param row - The report row, read for status.
 * @param rowIndex - The row's index, unused.
 * @param formatExtraData - Table extras carrying isLoadedData.
 * @returns The zero-padded report id or a loading skeleton.
 */
const reportIdFormatter = (
  cell: string,
  row: InstantReportTableItem,
  rowIndex: number,
  formatExtraData: any,
) => {
  const { isLoadedData } = formatExtraData;
  const isDisabled = row.status !== INSTANT_REPORT_STATUS.GENERATED;
  return isLoadedData ? (
    isDisabled ? (
      <span className="disable-column">
        {!_.isNil(cell) ? zeroPad(cell) : DOUBLE_HYPHEN_BULLET}
      </span>
    ) : (
      <span>{!_.isNil(cell) ? zeroPad(cell) : DOUBLE_HYPHEN_BULLET}</span>
    )
  ) : (
    <Skeleton count={1} />
  );
};

/**
 * Renders the date-of-service cell of the instant-report table. Shows a skeleton until the table reports loaded data, then emits the cell greyed out with the disable-column class whenever the report's status is not GENERATED.
 *
 * @param cell - The pre-formatted date-of-service range.
 * @param row - The report row, read for status.
 * @param rowIndex - The row's index, unused.
 * @param formatExtraData - Table extras carrying isLoadedData.
 * @returns The date-of-service text or a loading skeleton.
 */
const dateOfServiceFormatter = (
  cell: string,
  row: InstantReportTableItem,
  rowIndex: number,
  formatExtraData: any,
) => {
  const { isLoadedData } = formatExtraData;
  const isDisabled = row.status !== INSTANT_REPORT_STATUS.GENERATED;
  return isLoadedData ? (
    isDisabled ? (
      <span className="disable-column">{cell}</span>
    ) : (
      <span>{cell}</span>
    )
  ) : (
    <Skeleton count={1} />
  );
};

/**
 * Renders the generated-time cell of the instant-report table. Shows a skeleton until formatExtraData reports loaded data, then emits the pre-formatted cell value verbatim.
 *
 * @param cell - The pre-formatted generation timestamp.
 * @param row - The report row, unused.
 * @param rowIndex - The row's index, unused.
 * @param formatExtraData - Table extras carrying isLoadedData.
 * @returns The generated-time text or a loading skeleton.
 */
const generatedTimeFormatter = (
  cell: string,
  row: InstantReportTableItem,
  rowIndex: number,
  formatExtraData: any,
) => {
  const { isLoadedData } = formatExtraData;
  return isLoadedData ? <span>{cell}</span> : <Skeleton count={1} />;
};

/**
 * Renders the analysis-time cell of the instant-report table. Shows a skeleton until the table reports loaded data, formats a non-empty cell of seconds through getDuration into a day/hour/minute string, and falls back to N/A when the cell is empty.
 *
 * @param cell - The analysis time in seconds.
 * @param row - The report row, unused.
 * @param rowIndex - The row's index, unused.
 * @param formatExtraData - Table extras carrying isLoadedData.
 * @returns The formatted duration, N/A, or a loading skeleton.
 */
const analysisTimeFormatter = (
  cell: string,
  row: InstantReportTableItem,
  rowIndex: number,
  formatExtraData: any,
) => {
  const { isLoadedData } = formatExtraData;
  if (isLoadedData) {
    if (cell) {
      return <span>{getDuration(cell, 'd[d] hh[h] mm[m]', 'h', 'seconds')}</span>;
    }

    return <span>{t`N/A`}</span>;
  }

  return <Skeleton count={1} />;
};

/**
 * Renders the generated-by cell of the instant-report table. Shows a skeleton until the table reports loaded data, then labels the row Clinic when the cell holds a creator and Auto when it is empty.
 *
 * @param cell - The creating user, empty for system-generated reports.
 * @param row - The report row, unused.
 * @param rowIndex - The row's index, unused.
 * @param formatExtraData - Table extras carrying isLoadedData.
 * @returns The Clinic or Auto label, or a loading skeleton.
 */
const generatedByFormatter = (
  cell: string,
  row: InstantReportTableItem,
  rowIndex: number,
  formatExtraData: any,
) => {
  const { isLoadedData } = formatExtraData;
  return isLoadedData ? (
    <span>{!_.isEmpty(cell) ? t`Clinic` : t`Auto`}</span>
  ) : (
    <Skeleton count={1} />
  );
};

/**
 * Renders the status cell of the instant-report table. Shows a skeleton until the table reports loaded data, then an Artifact badge for artifact reports, a tooltipped Generating badge with an info icon while the status is not GENERATED, and a Generated badge otherwise.
 *
 * @param cell - The status value, unused in favour of the row fields.
 * @param row - The report row, read for isArtifactReport and status.
 * @param rowIndex - The row's index, unused.
 * @param formatExtraData - Table extras carrying isLoadedData.
 * @returns The status badge or a loading skeleton.
 */
const statusFormatterWithActionFormatter = (
  cell: string,
  row: InstantReportTableItem,
  rowIndex: number,
  formatExtraData: any,
) => {
  const { isLoadedData } = formatExtraData;
  if (isLoadedData) {
    if (row.isArtifactReport) {
      return (
        <div className="status-column status-column--artifact">
          <span>{t`Artifact`}</span>
        </div>
      );
    }

    if (row.status !== INSTANT_REPORT_STATUS.GENERATED) {
      return (
        <CustomTooltip
          title={t`Report is being generated. It will be ready within an hour.`}
          placement="bottom"
        >
          <div className="status-column status-column--generating">
            <span>{t`Generating`}</span>
            <ReactSVG src={InfoIcon} aria-hidden="true" />
          </div>
        </CustomTooltip>
      );
    }

    return <div className="status-column status-column--generated">{t`Generated`}</div>;
  }

  return <Skeleton count={1} />;
};

/**
 * Renders the view-report action of an instant-report table row. Shows a skeleton until formatExtraData reports loaded data, disables the eye button when the row is an artifact report or its status is not GENERATED, swaps the tooltip between the all-artifact, still-generating and view-report messages, and calls formatExtraData.onClickReport with the row on click.
 *
 * @param cell - The cell value, unused by this column.
 * @param row - The report row, read for status and isArtifactReport.
 * @param rowIndex - The row's index, unused.
 * @param formatExtraData - Table extras carrying isLoadedData and the onClickReport callback.
 * @returns The action button or a loading skeleton.
 */
const actionFormater = (
  cell: string,
  row: InstantReportTableItem,
  rowIndex: number,
  formatExtraData: any,
) => {
  const { isLoadedData } = formatExtraData;
  if (isLoadedData) {
    const { onClickReport } = formatExtraData;
    const isGenerating = row.status !== INSTANT_REPORT_STATUS.GENERATED;
    const disabled = row.isArtifactReport || isGenerating;
    return (
      <CustomButton
        id="view-instant-report-btn"
        className="view-instant-report-btn"
        tooltipTitle={
          disabled
            ? row.isArtifactReport
              ? t`This report could not be generated due to all data being artifact.`
              : isGenerating
                ? t`Please wait while the report is being generated.`
                : undefined
            : t`View report`
        }
        tooltipPlacement="bottom"
        icon={<ReactSVG src={eyeIcon} />}
        onClick={() => {
          onClickReport(row);
        }}
        disabled={disabled}
        ariaLabel={
          disabled
            ? row.isArtifactReport
              ? t`This report could not be generated due to all data being artifact.`
              : isGenerating
                ? t`Please wait while the report is being generated.`
                : undefined
            : t`View report`
        }
      />
    );
  }

  return <Skeleton count={1} />;
};

export {
  actionFormater,
  analysisTimeFormatter,
  dateOfServiceFormatter,
  formatInstantReport,
  formatInstantReports,
  generatedByFormatter,
  generatedTimeFormatter,
  reportIdFormatter,
  statusFormatterWithActionFormatter,
};
